import { getLogger } from '@logtape/logtape';
import { useEffect, useState } from 'react';

import { PrismicDataType, PrismicReturnType } from '@/models/PrismicHookTypes.ts';

import { usePrismicCache, usePrismicFetchingStore, usePrismicRefStore } from '@/stores/prismicStore.ts';

import { defaultFetcher, fetchers, isPrismicDataType } from '@/utils/prismicUtils.ts';

const logger = getLogger(['hook', 'prismic-fetching']);

interface RetryItem {
    type: PrismicDataType;
    attempt: number;
    next_retry_at: number;
}

interface RetryAttempt {
    type: PrismicDataType;
    success: boolean;
    attempt: number;
}

interface PrismicFetchResult<T extends PrismicDataType> {
    type: T;
    success: boolean;
}

const MAX_RETRY_ATTEMPTS = 5;
const BASE_RETRY_DELAY = 1000; // 1 second

const usePrismicFetching = (): void => {
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [retryQueue, setRetryQueue] = useState<RetryItem[]>([]);
    const fetchingQueue = usePrismicFetchingStore((state) => state.fetching_queue);
    const { master_ref, is_ref_set } = usePrismicRefStore();

    const fetchPrismicData = async <T extends PrismicDataType>(type: T): Promise<PrismicFetchResult<T>> => {
        const fetcher = fetchers[type] ?? ((): Promise<PrismicReturnType[T]> => defaultFetcher(type));

        try {
            const data: PrismicReturnType[T] = await fetcher(type);

            usePrismicCache.setState({
                [type]: {
                    data: data,
                    ref: master_ref,
                },
            });

            return { type: type, success: true };
        } catch (error) {
            logger.error('Failed to fetch data of type {type}', { type: type, error: error });
            return {
                type: type,
                success: false,
            };
        }
    };

    const addToRetryQueue = (type: PrismicDataType, attempt: number = 1): void => {
        if (attempt > MAX_RETRY_ATTEMPTS) {
            logger.error('Max retry attempts ({maxAttempts}) reached for type {type}. Giving up.', {
                maxAttempts: MAX_RETRY_ATTEMPTS,
                type: type,
            });
            return;
        }

        const delay = BASE_RETRY_DELAY * attempt;
        const next_retry_at = Date.now() + delay;

        setRetryQueue((prev) => {
            // Remove any existing retry item for this type
            const filtered = prev.filter((item) => item.type !== type);
            return [...filtered, { type, attempt, next_retry_at }];
        });

        logger.debug('Added {type} to retry queue. Attempt {attempt}/{maxAttempts}, next retry in {delayMs}ms', {
            type: type,
            attempt: attempt,
            maxAttempts: MAX_RETRY_ATTEMPTS,
            delayMs: delay,
        });
    };

    const processRetryQueue = async (): Promise<void> => {
        const now = Date.now();
        const readyToRetry = retryQueue.filter((item) => item.next_retry_at <= now);

        if (readyToRetry.length === 0) return;

        logger.debug('Processing {itemCount} items from retry queue', { itemCount: readyToRetry.length });

        const promises: Promise<RetryAttempt>[] = readyToRetry.map(async (item) => {
            const result = await fetchPrismicData(item.type);
            return {
                type: item.type,
                success: result.success,
                attempt: item.attempt,
            };
        });

        const results = await Promise.all(promises);
        const processedTypes = new Set<PrismicDataType>();

        for (const result of results) {
            processedTypes.add(result.type);

            if (result.success) {
                logger.debug('Successfully fetched {type} on retry attempt {attempt}', {
                    type: result.type,
                    attempt: result.attempt,
                });
            } else {
                // Retry failed, add back to retry queue with incremented attempt
                addToRetryQueue(result.type, result.attempt + 1);
            }
        }

        // Remove processed items from retry queue
        setRetryQueue((prev) => prev.filter((item) => !processedTypes.has(item.type)));
    };

    const processQueue = async (): Promise<void> => {
        setIsFetching(true);

        const fetchingTypes: Set<PrismicDataType> = new Set(fetchingQueue);

        const promises: Promise<PrismicFetchResult<PrismicDataType>>[] = [];
        for (const type of fetchingTypes) {
            if (!isPrismicDataType(type)) {
                logger.warn('Invalid Prismic data type {type}, will not fetch', { type: String(type) });
                continue;
            }

            promises.push(fetchPrismicData(type));
        }

        const results: PrismicFetchResult<PrismicDataType>[] = await Promise.all(promises);
        const successfullyFetched = new Set<PrismicDataType>();

        for (const result of results) {
            if (result.success) {
                successfullyFetched.add(result.type);
            } else {
                addToRetryQueue(result.type);
            }
        }

        usePrismicFetchingStore.setState((state) => ({
            fetching_queue: state.fetching_queue.filter((type) => !successfullyFetched.has(type)),
        }));
        setIsFetching(false);
    };

    // Effect for processing main queue
    useEffect(() => {
        // no antipattern here, since queue might be triggered from different sources
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (isFetching || !master_ref || !is_ref_set || fetchingQueue.length === 0) return;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        void processQueue();
    }, [master_ref, is_ref_set, fetchingQueue, isFetching]);

    // Effect for processing retry queue (runs independently)
    useEffect(() => {
        if (retryQueue.length === 0 || !master_ref || !is_ref_set) return undefined;

        const interval = setInterval(() => {
            void processRetryQueue();
        }, 1000); // Check every second for items ready to retry

        return (): void => clearInterval(interval);
    }, [retryQueue, master_ref, is_ref_set]);
};

export default usePrismicFetching;
